<template>
    <div
        ref="iframeContainerRef"
        v-show="post.play_json?.length > 0"
        class="relative flex items-center mb-4"
    >
        <img
            v-if="thumbnailUrl"
            :src="thumbnailUrl"
            alt=""
            :style="{
                backgroundImage: `linear-gradient(to bottom, #fffa, #fffa), url('${
                    post.background ?? thumbnailUrl
                }')`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                aspectRatio: 2,
            }"
            class="max-w-full lg:max-w-[630px]"
        />
        <iframe
            width="100%"
            height="auto"
            class="border rounded-md border-gray-200"
            style="aspect-ratio: 2 / 1"
            allowfullscreen
            v-if="!thumbnailUrl && post.play_json?.length > 0"
            :src="`${whiteboardUrl}?whiteboardid=${state.whiteboardId}&readonly=true&isforum=true`"
            frameborder="0"
        ></iframe>
        <simple-loading-circle
            v-if="state.loadingWhiteboard"
            class="absolute"
        />
    </div>
    <div v-bind="$attrs" class="tiptap-display">
        <editor-content :editor="editor" />
    </div>
    <div class="my-6" v-if="firstResolvedLink">
        <a
            :href="firstResolvedLink.url"
            class="
                block
                bg-gray-100
                rounded-md
                p-4
                shadow
                md:max-w-[50%]
                max-w-full
            "
            target="_blank"
            rel="noreferrer noopener"
        >
            <img
                v-if="firstResolvedLink.image"
                :src="firstResolvedLink.image"
                alt="Link image"
                class="aspect-[2/1] rounded-lg w-full h-auto object-cover mb-2"
            />
            <div class="font-semibold">
                {{ firstResolvedLink.title }}
            </div>
            <div class="text-xs opacity-50 mt-2 break-all">
                {{ firstResolvedLink.description }}
            </div>
            <div
                class="
                    block
                    text-xs
                    opacity-50
                    text-ellipsis
                    overflow-hidden
                    break-all
                    w-full
                    mt-2
                    hover:underline
                "
            >
                {{ firstResolvedLink.url }}
            </div>
        </a>
    </div>
</template>

<script setup>
import { EditorContent, useEditor } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Link } from "@tiptap/extension-link";
import { computed, nextTick, onMounted, reactive, ref } from "vue";
import { Mention } from "@tiptap/extension-mention";
import { mentionSuggestion } from "./tiptap/suggestion";
import SimpleLoadingCircle from "../SimpleLoadingCircle.vue";

const props = defineProps({
    post: Object,
    whiteboardUrl: String,
    thumbnailUrl: [String, null],
});

const firstResolvedLink = computed(() => {
    try {
        const meta = JSON.parse(props.post?.meta);
        if (meta?.firstResolvedLink) {
            return meta.firstResolvedLink;
        }
    } catch (e) {
        return null;
    }
});

const state = reactive({
    whiteboardId: crypto.randomUUID(),
    loadingWhiteboard: false,
});

const iframeContainerRef = ref(null);

const editor = useEditor({
    editable: false,
    extensions: [
        StarterKit,
        Placeholder.configure({
            placeholder: props.placeholder,
        }),
        Link.configure({
            openOnClick: true,
            HTMLAttributes: {
                class: "text-blue-500 hover:underline",
            },
        }),
        Mention.configure({
            suggestion: mentionSuggestion,
            HTMLAttributes: {
                class: "font-semibold mx-2 text-blue-800 bg-blue-100 rounded-md px-2 py-1",
            },
            renderLabel: (props) => {
                return `@${props.node.attrs.id.name}`;
            },
        }),
    ],
    editorProps: {
        attributes: {
            class: "outline-none",
        },
    },
    content: JSON.parse(props.post.content),
});

onMounted(() => {
    if (!props.thumbnailUrl && props.post?.play_json?.length > 0) {
        nextTick(() => {
            state.loadingWhiteboard = true;
            const iframe = iframeContainerRef.value.querySelector("iframe");
            const contentWindow = iframe.contentWindow;
            const interval = setInterval(() => {
                contentWindow.postMessage(
                    { action: "ping", id: state.whiteboardId },
                    props.whiteboardUrl
                );
            }, 1000);
            window.addEventListener("message", (event) => {
                if (
                    event.data.action === "pong" &&
                    event.data.id === state.whiteboardId
                ) {
                    clearInterval(interval);

                    contentWindow.postMessage(
                        JSON.parse(props.post.play_json),
                        props.whiteboardUrl
                    );
                    if (props.post.background) {
                        contentWindow.postMessage(
                            {
                                action: "background",
                                background:
                                    window.origin + props.post?.background,
                            },
                            props.whiteboardUrl
                        );
                    }
                    state.loadingWhiteboard = false;
                }
            });
        });
    }
});
</script>

<style>
.tiptap-display .ProseMirror p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: #adb5bd;
    pointer-events: none;
    height: 0;
}

.tiptap-display .ProseMirror code {
    background: #d0dae0;
    color: #1e4964;
    padding: 4px 6px;
    border-radius: 4px;
    font-size: 0.75em;
    font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier,
        monospace;
}

.tiptap-display .ProseMirror a {
    /* These are technically the same, but use both */
    overflow-wrap: break-word;
    word-wrap: break-word;

    -ms-word-break: break-all;
    /* This is the dangerous one in WebKit, as it breaks things wherever */
    word-break: break-all;
    /* Instead use this non-standard one: */
    word-break: break-word;

    /* Adds a hyphen where the word breaks, if supported (No Blink) */
    -ms-hyphens: auto;
    -moz-hyphens: auto;
    -webkit-hyphens: auto;
    hyphens: auto;
}
</style>
